import { useState } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/cn";
import { ChevronLeft, ChevronRight } from "@/icons";

// Calendar — dependency-free month grid. Single-date or range selection.
export interface CalendarRange {
  from?: Date;
  to?: Date;
}
export interface CalendarProps {
  mode?: "single" | "range";
  selected?: Date | null;
  onSelect?: (d: Date) => void;
  range?: CalendarRange;
  onRangeChange?: (r: CalendarRange) => void;
  min?: Date;
  max?: Date;
  className?: string;
}

const DOW_KEYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"] as const;
const startOfMonth = (d: Date) => new Date(d.getFullYear(), d.getMonth(), 1);
const addMonths = (d: Date, n: number) => new Date(d.getFullYear(), d.getMonth() + n, 1);
const stripTime = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const sameDay = (a?: Date | null, b?: Date | null) =>
  !!a && !!b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
const inRange = (d: Date, from?: Date, to?: Date) =>
  !!from && !!to && stripTime(d).getTime() > stripTime(from).getTime() && stripTime(d).getTime() < stripTime(to).getTime();

export function Calendar({ mode = "single", selected, onSelect, range, onRangeChange, min, max, className }: CalendarProps) {
  const { t, i18n } = useTranslation("ui");
  const seed = selected || range?.from || new Date();
  const [view, setView] = useState(() => startOfMonth(seed));
  const today = new Date();

  const gridStart = new Date(view);
  gridStart.setDate(1 - view.getDay());
  const days = Array.from({ length: 42 }, (_, i) => {
    const d = new Date(gridStart);
    d.setDate(gridStart.getDate() + i);
    return d;
  });

  const disabled = (d: Date) =>
    (min && stripTime(d) < stripTime(min)) || (max && stripTime(d) > stripTime(max));

  const onPick = (d: Date) => {
    if (disabled(d)) return;
    if (mode === "single") {
      onSelect?.(d);
      return;
    }
    const { from, to } = range ?? {};
    if (!from || (from && to)) onRangeChange?.({ from: d, to: undefined });
    else if (stripTime(d) >= stripTime(from)) onRangeChange?.({ from, to: d });
    else onRangeChange?.({ from: d, to: undefined });
  };

  return (
    <div className={cn("w-64 select-none", className)}>
      <div className="mb-2 flex items-center justify-between">
        <button
          type="button"
          aria-label={t("calendar.previousMonth")}
          onClick={() => setView(addMonths(view, -1))}
          className="grid h-8 w-8 place-items-center rounded-lg text-fg-muted hover:bg-surface-2 hover:text-fg"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <div className="text-sm font-semibold">
          {view.toLocaleString(i18n.language, { month: "long", year: "numeric" })}
        </div>
        <button
          type="button"
          aria-label={t("calendar.nextMonth")}
          onClick={() => setView(addMonths(view, 1))}
          className="grid h-8 w-8 place-items-center rounded-lg text-fg-muted hover:bg-surface-2 hover:text-fg"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
      <div className="mb-1 grid grid-cols-7 text-center text-2xs font-medium text-fg-subtle">
        {DOW_KEYS.map((k) => (
          <div key={k}>{t(`calendar.dow.${k}`)}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-y-0.5">
        {days.map((d, i) => {
          const outside = d.getMonth() !== view.getMonth();
          const isSel = mode === "single" ? sameDay(d, selected) : sameDay(d, range?.from) || sameDay(d, range?.to);
          const between = mode === "range" && inRange(d, range?.from, range?.to);
          const dis = disabled(d);
          return (
            <button
              key={i}
              type="button"
              disabled={dis}
              aria-pressed={isSel}
              onClick={() => onPick(d)}
              className={cn(
                "mx-auto grid h-9 w-9 place-items-center rounded-lg text-sm transition-colors",
                dis && "pointer-events-none opacity-30",
                isSel
                  ? "bg-primary font-semibold text-primary-fg"
                  : between
                    ? "bg-primary/15 text-fg"
                    : outside
                      ? "text-fg-subtle hover:bg-surface-2"
                      : "text-fg hover:bg-surface-2",
                !isSel && sameDay(d, today) && "ring-1 ring-inset ring-border-strong",
              )}
            >
              {d.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
}
